import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module definitions with icons
export const MODULES = {
  grc: {
    id: 'grc',
    name: 'GRC Suite',
    icon: 'Shield',
    color: '#10B981',
    path: '/dashboard'
  },
  contracts: {
    id: 'contracts',
    name: 'Contract Manager',
    icon: 'FileText',
    color: '#F59E0B',
    path: '/contracts'
  },
  pmo: {
    id: 'pmo',
    name: 'PMO',
    icon: 'Briefcase',
    color: '#6366F1',
    path: '/pmo'
  },
  ma: {
    id: 'ma',
    name: 'M&A',
    icon: 'TrendingUp',
    color: '#EC4899',
    path: '/ma'
  },
  board: {
    id: 'board',
    name: 'Board Management',
    icon: 'Users',
    color: '#8B5CF6',
    path: '/board'
  },
  cosec: {
    id: 'cosec',
    name: 'Company Secretary',
    icon: 'CalendarCheck',
    color: '#06B6D4',
    path: '/cosec'
  }
};

export const useEntitlements = () => {
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntitlements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/modules/entitlements`);
      setEntitlements(response.data);
      setError(null);
    } catch (err) {
      // If 401, user not logged in - that's okay
      if (err.response?.status !== 401) {
        setError(err.response?.data?.detail || 'Failed to load entitlements');
      }
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      fetchEntitlements();
    } else {
      setLoading(false);
    }
  }, [fetchEntitlements]);

  const hasModule = useCallback((module) => {
    if (!entitlements) return false;
    return !!entitlements.modules?.[module];
  }, [entitlements]);

  const getModuleTier = useCallback((module) => {
    if (!entitlements) return null;
    return entitlements.modules?.[module]?.tier || null;
  }, [entitlements]);

  const canAccess = useCallback((module, minTier = 'standard') => {
    const tier = getModuleTier(module);
    if (!tier) return false;
    
    const tierOrder = { standard: 1, pro: 2, pro_plus: 3 };
    return (tierOrder[tier] || 0) >= (tierOrder[minTier] || 1);
  }, [getModuleTier]);

  const isLocked = useCallback((module) => {
    return !hasModule(module);
  }, [hasModule]);

  const getLockedModules = useCallback(() => {
    return entitlements?.locked_modules || [];
  }, [entitlements]);

  const getActiveModules = useCallback(() => {
    if (!entitlements?.modules) return [];
    return Object.entries(entitlements.modules).map(([id, data]) => ({
      id,
      ...MODULES[id],
      ...data
    }));
  }, [entitlements]);

  const hasFullSuite = useCallback(() => {
    return entitlements?.has_full_suite || false;
  }, [entitlements]);

  const refresh = useCallback(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  return {
    entitlements,
    loading,
    error,
    hasModule,
    getModuleTier,
    canAccess,
    isLocked,
    getLockedModules,
    getActiveModules,
    hasFullSuite,
    refresh
  };
};

// Hook for module pricing
// Fallback pricing data if API fails/slow
// Pricing per the strategy doc: Standard £299/mo, Pro £499/mo, Pro+ POA
const DEFAULT_PRICING = [
  { module: 'grc', name: 'GRC Suite', description: 'Governance, Risk & Compliance management', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'contracts', name: 'Contract Manager', description: 'Contract lifecycle management', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'pmo', name: 'PMO', description: 'Project Management Office', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'ma', name: 'M&A', description: 'Mergers & Acquisitions deal management', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'ma_plus', name: 'M&A Pro+', description: 'Advanced M&A due diligence & comparison', tiers: { pro: { monthly: 0, label: 'POA' } } },
  { module: 'board', name: 'Board Management', description: 'Board packs, meetings & governance', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'cosec', name: 'Company Secretary', description: 'Corporate governance & statutory compliance', tiers: { standard: { monthly: 299 }, pro: { monthly: 499 } } },
  { module: 'full', name: 'Full Suite', description: 'All modules included', tiers: { standard: { monthly: 299 }, pro: { monthly: 499, original_monthly: 599 } } }
];

export const useModulePricing = () => {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/modules/pricing`);
        if (response.data.pricing?.length > 0) {
          setPricing(response.data.pricing);
        }
      } catch (err) {
        console.error('Failed to load pricing, using defaults:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  const getModulePrice = useCallback((module, tier = 'standard', cycle = 'monthly') => {
    const moduleData = pricing.find(p => p.module === module);
    if (!moduleData) return null;
    return moduleData.tiers?.[tier]?.[cycle] || null;
  }, [pricing]);

  return { pricing, loading, getModulePrice };
};

export default useEntitlements;
